import React, { useEffect, useState, useRef, useMemo } from "react";
import { Plus, Trash, Play, Pause, ArrowCounterClockwise, Barbell as BarbellIcon, X, Share, Lightning, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import ShareCardModal from "@/components/ShareCardModal";

const CATEGORIES = ["Strength", "Cardio", "HIIT", "Mobility", "Calisthenics", "Other"];

function Timer() {
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [running]);

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  return (
    <div className="tactical-card p-6 bg-black/60" data-testid="workout-timer">
      <div className="label-tiny mb-3">// SESSION TIMER</div>
      <div className="font-heading text-6xl tabular-nums text-brand-accent neon-yellow mb-4">
        {mm}:{ss}
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => setRunning((r) => !r)}
          className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest flex items-center gap-2"
          data-testid="timer-toggle-button"
        >
          {running ? <><Pause size={14} weight="fill" /> PAUSE</> : <><Play size={14} weight="fill" /> START</>}
        </button>
        <button
          onClick={() => { setRunning(false); setSeconds(0); }}
          className="border border-white/10 hover:border-white/40 px-4 py-2 text-xs label-tiny tracking-widest flex items-center gap-2"
          data-testid="timer-reset-button"
        >
          <ArrowCounterClockwise size={14} /> RESET
        </button>
      </div>
    </div>
  );
}

export default function Workouts() {
  const [workouts, setWorkouts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "Strength", duration_min: 30, notes: "" });
  const [exercises, setExercises] = useState([{ name: "", sets: 3, reps: "10", weight: 0 }]);
  const [templates, setTemplates] = useState([]);
  const [sharePR, setSharePR] = useState(null);
  const [quickBusy, setQuickBusy] = useState(null);

  const load = () => api.get("/workouts").then(({ data }) => setWorkouts(data)).catch(() => {});
  const loadTemplates = () => api.get("/templates").then(({ data }) => setTemplates(data)).catch(() => {});
  useEffect(() => { load(); loadTemplates(); }, []);

  // One-tap quick-log: re-logs a past session / template as a new workout today.
  // Reuses POST /workouts so PR auto-detection + coach notifications still fire.
  const quickLog = async (key, payload) => {
    if (quickBusy) return;
    setQuickBusy(key);
    try {
      const { data } = await api.post("/workouts", {
        name: payload.name,
        category: payload.category || "Strength",
        duration_min: Number(payload.duration_min) || 0,
        exercises: (payload.exercises || []).filter((e) => e.name),
        notes: payload.notes || "",
      });
      toast.success(`Logged "${payload.name}"`);
      if (data.new_prs?.length > 0) {
        data.new_prs.forEach((pr) => {
          toast.success(`🏆 NEW PR! ${pr.exercise}: ${pr.value} ${pr.unit}`, { duration: 6000 });
        });
      }
      load();
    } catch {
      toast.error("Failed to log");
    } finally {
      setQuickBusy(null);
    }
  };

  // Distinct recent sessions (by name), most recent first — the quick-log menu.
  const recentQuick = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const w of workouts) {
      const k = (w.name || "").trim().toLowerCase();
      if (!k || seen.has(k)) continue;
      seen.add(k);
      out.push(w);
      if (out.length >= 6) break;
    }
    return out;
  }, [workouts]);
  const lastWorkout = workouts[0] || null;

  const addExercise = () => setExercises([...exercises, { name: "", sets: 3, reps: "10", weight: 0 }]);
  const removeExercise = (i) => setExercises(exercises.filter((_, idx) => idx !== i));
  const updateExercise = (i, key, val) => {
    const next = [...exercises];
    next[i][key] = val;
    setExercises(next);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Workout name required");
    try {
      const { data } = await api.post("/workouts", {
        ...form,
        duration_min: Number(form.duration_min) || 0,
        exercises: exercises.filter((e) => e.name.trim()),
      });
      toast.success("Workout logged.");
      // PR notifications
      if (data.new_prs?.length > 0) {
        data.new_prs.forEach((pr) => {
          toast.success(`🏆 NEW PR! ${pr.exercise}: ${pr.value} ${pr.unit}`, { duration: 6000 });
        });
      }
      setOpen(false);
      setForm({ name: "", category: "Strength", duration_min: 30, notes: "" });
      setExercises([{ name: "", sets: 3, reps: "10", weight: 0 }]);
      load();
    } catch {
      toast.error("Failed to save");
    }
  };

  const saveAsTemplate = async () => {
    if (!form.name.trim()) return toast.error("Workout name required");
    const validExercises = exercises.filter((e) => e.name.trim());
    if (validExercises.length === 0) return toast.error("Add at least one exercise");
    try {
      await api.post("/templates", {
        name: form.name,
        category: form.category,
        exercises: validExercises,
        notes: form.notes,
      });
      toast.success("Saved as template.");
      loadTemplates();
    } catch {
      toast.error("Failed");
    }
  };

  const applyTemplate = (t) => {
    setForm({ name: t.name, category: t.category, duration_min: 30, notes: t.notes || "" });
    setExercises(t.exercises.length > 0 ? t.exercises : [{ name: "", sets: 3, reps: "10", weight: 0 }]);
    setOpen(true);
  };

  const deleteTemplate = async (id) => {
    await api.delete(`/templates/${id}`);
    toast.success("Template removed");
    loadTemplates();
  };

  const remove = async (id) => {
    await api.delete(`/workouts/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="workouts-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-tiny text-brand-accent mb-2">// TRAINING LOG</div>
          <h1 className="font-heading text-4xl sm:text-5xl">WORKOUTS</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-kinetic px-5 py-3 text-xs label-tiny tracking-widest flex items-center gap-2" data-testid="add-workout-button">
              <Plus size={14} weight="bold" /> NEW WORKOUT
            </button>
          </DialogTrigger>
          <DialogContent className="bg-surface-DEFAULT border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="workout-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">LOG WORKOUT</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="label-tiny block mb-2">NAME</label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Upper body push" className="bg-surface-elevated border-white/10" data-testid="workout-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-tiny block mb-2">CATEGORY</label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="bg-surface-elevated border-white/10" data-testid="workout-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface-DEFAULT border-white/10 text-white">
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="label-tiny block mb-2">DURATION (MIN)</label>
                  <Input type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className="bg-surface-elevated border-white/10" data-testid="workout-duration-input" />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label-tiny">EXERCISES</label>
                  <button onClick={addExercise} className="text-xs text-brand-accent hover:underline flex items-center gap-1" data-testid="add-exercise-button">
                    <Plus size={12} /> ADD
                  </button>
                </div>
                <div className="space-y-2">
                  {exercises.map((ex, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`exercise-row-${i}`}>
                      <Input className="col-span-5 bg-surface-elevated border-white/10" placeholder="Exercise" value={ex.name} onChange={(e) => updateExercise(i, "name", e.target.value)} data-testid={`exercise-name-${i}`} />
                      <Input className="col-span-2 bg-surface-elevated border-white/10" type="number" placeholder="Sets" value={ex.sets} onChange={(e) => updateExercise(i, "sets", e.target.value)} data-testid={`exercise-sets-${i}`} />
                      <Input className="col-span-2 bg-surface-elevated border-white/10" placeholder="Reps" value={ex.reps} onChange={(e) => updateExercise(i, "reps", e.target.value)} data-testid={`exercise-reps-${i}`} />
                      <Input className="col-span-2 bg-surface-elevated border-white/10" type="number" placeholder="Wt" value={ex.weight} onChange={(e) => updateExercise(i, "weight", e.target.value)} data-testid={`exercise-weight-${i}`} />
                      <button onClick={() => removeExercise(i)} className="col-span-1 text-text-secondary hover:text-red-400" data-testid={`remove-exercise-${i}`}>
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="label-tiny block mb-2">NOTES</label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-surface-elevated border-white/10 min-h-[80px]" data-testid="workout-notes-input" />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button onClick={saveAsTemplate} className="border border-white/10 hover:border-brand-primary py-3 text-xs label-tiny tracking-widest transition-colors" data-testid="save-template-button">
                  SAVE AS TEMPLATE
                </button>
                <button onClick={save} className="btn-kinetic py-3 text-xs label-tiny tracking-widest" data-testid="save-workout-button">
                  SAVE WORKOUT
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* ── ONE-TAP QUICK LOG ───────────────────────────────── */}
      {(lastWorkout || templates.length > 0) && (
        <div className="tactical-card p-5 mb-6" data-testid="quick-log-section">
          <div className="flex items-center gap-2 mb-3">
            <Lightning size={16} weight="fill" className="text-brand-accent" />
            <span className="label-tiny text-brand-accent">// QUICK LOG · ONE TAP</span>
          </div>
          <p className="text-xs text-text-secondary mb-4">
            Done the same session again? Tap once to log it with today&apos;s date — no forms.
          </p>

          {lastWorkout && (
            <button
              onClick={() => quickLog("last", lastWorkout)}
              disabled={!!quickBusy}
              className="btn-kinetic w-full sm:w-auto px-5 py-3 text-xs label-tiny tracking-widest flex items-center justify-center gap-2 mb-4 disabled:opacity-50"
              data-testid="quick-log-repeat-last"
            >
              <ArrowClockwise size={14} weight="bold" />
              {quickBusy === "last" ? "LOGGING..." : `REPEAT LAST · ${lastWorkout.name?.toUpperCase()}`}
            </button>
          )}

          {recentQuick.length > 0 && (
            <>
              <div className="label-tiny text-text-secondary mb-2">RECENT SESSIONS</div>
              <div className="flex flex-wrap gap-2 mb-4">
                {recentQuick.map((w) => (
                  <button
                    key={w.log_id}
                    onClick={() => quickLog(`w-${w.log_id}`, w)}
                    disabled={!!quickBusy}
                    className="group inline-flex items-center gap-2 border border-slate-200 hover:border-brand-accent hover:bg-brand-accent/[0.06] px-3 py-2 transition-colors disabled:opacity-50"
                    data-testid={`quick-log-recent-${w.log_id}`}
                  >
                    <Lightning size={12} weight="fill" className="text-brand-accent" />
                    <span className="text-xs font-heading">{w.name}</span>
                    <span className="label-tiny text-[9px] text-text-secondary">
                      {quickBusy === `w-${w.log_id}` ? "..." : `${w.category} · ${w.exercises?.length || 0} EX`}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}

          {templates.length > 0 && (
            <>
              <div className="label-tiny text-text-secondary mb-2">FROM TEMPLATES</div>
              <div className="flex flex-wrap gap-2">
                {templates.map((t) => (
                  <button
                    key={t.template_id}
                    onClick={() => quickLog(`t-${t.template_id}`, { ...t, duration_min: 30 })}
                    disabled={!!quickBusy}
                    className="inline-flex items-center gap-2 border border-slate-200 hover:border-brand-primary hover:bg-brand-primary/[0.06] px-3 py-2 transition-colors disabled:opacity-50"
                    data-testid={`quick-log-template-${t.template_id}`}
                  >
                    <Lightning size={12} weight="fill" className="text-brand-primary" />
                    <span className="text-xs font-heading">{t.name}</span>
                    <span className="label-tiny text-[9px] text-text-secondary">
                      {quickBusy === `t-${t.template_id}` ? "..." : `${t.exercises?.length || 0} EX`}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-6">
          <Timer />
          {templates.length > 0 && (
            <div className="tactical-card p-5" data-testid="templates-section">
              <div className="label-tiny mb-3">// TEMPLATES</div>
              <div className="space-y-2">
                {templates.map((t) => (
                  <div key={t.template_id} className="flex items-center justify-between gap-2 border border-white/5 p-2.5 hover:bg-surface-elevated transition-colors" data-testid={`template-${t.template_id}`}>
                    <button onClick={() => applyTemplate(t)} className="flex-1 text-left">
                      <div className="text-sm font-heading">{t.name}</div>
                      <div className="label-tiny text-brand-accent">{t.category} · {t.exercises.length} ex</div>
                    </button>
                    <button onClick={() => deleteTemplate(t.template_id)} className="text-text-secondary hover:text-red-400" data-testid={`delete-template-${t.template_id}`}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="lg:col-span-2">
          {workouts.length === 0 ? (
            <div className="tactical-card p-12 text-center" data-testid="workouts-empty">
              <BarbellIcon size={36} weight="duotone" className="text-brand-primary mx-auto mb-4" />
              <h3 className="font-heading text-xl mb-2">NO WORKOUTS LOGGED</h3>
              <p className="text-sm text-text-secondary">Tap "NEW WORKOUT" to record your first session.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="workouts-list">
              {workouts.map((w) => (
                <div key={w.log_id} className="tactical-card p-5 hover:bg-surface-elevated transition-colors" data-testid={`workout-card-${w.log_id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-heading text-lg">{w.name}</h3>
                      <div className="flex items-center gap-3 text-xs text-text-secondary mt-1">
                        <span className="label-tiny text-brand-accent">{w.category}</span>
                        <span className="tabular-nums">{w.duration_min} MIN</span>
                        <span>{new Date(w.date).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <button onClick={() => remove(w.log_id)} className="text-text-secondary hover:text-red-400" data-testid={`delete-workout-${w.log_id}`}>
                      <Trash size={16} />
                    </button>
                  </div>
                  {w.exercises?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/5 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      {w.exercises.map((ex, i) => (
                        <div key={i} className="text-text-secondary">
                          <span className="text-white">{ex.name}</span>
                          {(ex.mode || "").toLowerCase() === "cardio"
                            ? ` · ${ex.intervals?.length ? `${ex.intervals.length}× · ` : ""}${ex.distance ? `${ex.distance}${ex.distance_unit || "m"}` : ""}${ex.time ? ` · ${ex.time}` : ""}${ex.load ? ` · ${ex.load}kg` : ""}`
                            : ` · ${ex.sets}×${ex.reps}${ex.weight ? ` @ ${ex.weight}` : ""}`}
                        </div>
                      ))}
                    </div>
                  )}
                  {w.notes && <p className="text-xs text-text-secondary mt-2 italic">"{w.notes}"</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ShareCardModal open={!!sharePR} onClose={() => setSharePR(null)} pr={sharePR} />
    </div>
  );
}
